export const CHECKOUT_URL = "https://augmentation-en.impultienda.ar/checkout/";
export const MOCKUP_URL =
  "https://cdn.magicpatterns.com/uploads/vkEspmXANtxDMaiRwByzwg/AUGMENTATION-EN.png";
export const DOCTOR_URL =
  "https://cdn.magicpatterns.com/uploads/9NW2GFNdvViMtm3S7Ajpz3/MICHAEL_CARTER_-_IMPLANTES.png";

export const competingThoughts = [
  "Patient objectives",
  "Visual observations",
  "Relevant case variables",
  "Asymmetries",
  "Available information",
  "Missing information",
  "Questions",
];

export const pathwaySteps = [
  {
    name: "Assess",
    questions: [
      "What information do you have?",
      "What information is missing?",
      "What are the patient's stated objectives?",
      "What context is relevant to the educational case?",
    ],
  },
  {
    name: "Observe",
    questions: [
      "What do you actually see?",
      "What deserves closer attention?",
      "What are you observing before making assumptions?",
    ],
  },
  {
    name: "Organize",
    questions: [
      "Where do your observations belong?",
      "How can you separate the different variables competing for your attention?",
    ],
  },
  {
    name: "Question",
    questions: [
      "What would you want to clarify?",
      "What information would you want to investigate further?",
      "What questions does the case raise?",
    ],
  },
  {
    name: "Prioritize",
    questions: [
      "Which observations deserve additional attention?",
      "Which uncertainties matter most for the educational review?",
    ],
  },
  {
    name: "Review",
    questions: [
      "How does your initial reasoning compare with the educational discussion?",
      "What did you notice?",
      "What did you interpret differently?",
    ],
  },
  {
    name: "Learn",
    questions: [
      "What did you notice?",
      "What did you miss?",
      "What will you review again?",
      "What can you carry forward to the next case?",
    ],
  },
];

export const painPoints = [
  {
    quote: "Where do I even start?",
    detail: "Multiple observations and variables compete for your attention.",
  },
  {
    quote: "I need to review everything before I can organize my thoughts.",
    detail: "You move between articles, notes, lectures and previous cases.",
  },
  {
    quote: "Am I looking at the case in the right order?",
    detail: "Without a repeatable framework, every case can feel different.",
  },
  {
    quote: "What am I missing?",
    detail:
      "You notice certain features but want a more structured way to identify information gaps.",
  },
  {
    quote: "I keep learning, but I need more opportunities to practice case analysis.",
    detail:
      "More information doesn't automatically create better opportunities to practice your reasoning.",
  },
  {
    quote: "I want to learn something from every case.",
    detail:
      "But without structured reflection, valuable observations can disappear after the review.",
  },
];

export type Volume = {
  number: string;
  title: string;
  intro: string[];
  listLabel?: string;
  items?: string[];
  outro?: string[];
  featured?: boolean;
  numbered?: boolean;
};

export const volumes: Volume[] = [
  {
    number: "I",
    title: "The 15-Minute Breast Augmentation Assessment Pathway™",
    intro: [
      "I wanted the first volume to answer one simple question: “Where do I start?”",
      "You'll receive the complete educational pathway I designed to organize your case review:",
    ],
    items: ["Assess", "Observe", "Organize", "Question", "Prioritize", "Review", "Learn"],
    outro: ["Plus the 15-Minute Case Analysis Sheet™ so you can practice the framework repeatedly."],
  },
  {
    number: "II",
    title: "The Breast Augmentation Assessment Map™",
    intro: [
      "When you're reviewing a breast augmentation case, it can be easy to let individual observations compete for your attention. This volume gives you a structured visual framework for organizing them.",
    ],
    listLabel: "You'll work through:",
    items: [
      "Patient objectives",
      "Available case information",
      "Relevant observations",
      "Case context",
      "Relevant variables",
      "Information gaps",
      "Questions requiring further review",
      "Documentation prompts",
      "Reflection points",
    ],
    outro: [
      "My goal here is simple: give you a better structure for looking at the case before jumping to conclusions.",
    ],
  },
  {
    number: "III",
    title: "The Breast Augmentation Case Review Workbook™",
    intro: ["This volume turns the pathway into something you can actually use repeatedly."],
    listLabel: "For each educational case, you'll work through:",
    items: [
      "Patient objectives",
      "Case context",
      "Initial observations",
      "Relevant variables",
      "Information gaps",
      "Questions",
      "Priorities",
      "Educational review",
      "What you noticed",
      "What you missed",
      "What you learned",
      "What you want to review again",
    ],
    outro: [
      "This is not a universal surgical recipe. It's a framework for organizing your professional thinking during educational case review.",
    ],
  },
  {
    number: "IV",
    title: "The Breast Augmentation Case Atlas™",
    featured: true,
    numbered: true,
    intro: [
      "This is the volume I expect you'll return to again and again.",
      "You'll receive 20 educational breast augmentation cases structured so you don't simply read the case and immediately look for the review. Instead:",
    ],
    items: [
      "Review the case.",
      "Write down what you notice.",
      "Identify the variables you consider relevant.",
      "Identify questions and information gaps.",
      "Prioritize what deserves additional review.",
      "Compare your reasoning with the educational review.",
      "Record what you learned.",
    ],
    outro: ["That's deliberate case practice."],
  },
  {
    number: "V",
    title: "The Difficult Breast Augmentation Case Review Pathway™",
    intro: [
      "Some educational cases deserve more thought than others. For these scenarios, I've created a structured review process.",
    ],
    listLabel: "You'll work through:",
    items: [
      "What information is available?",
      "What appears uncertain?",
      "What deserves additional review?",
      "What questions should be considered?",
      "What observations could easily be overlooked?",
      "What information would you want to clarify?",
      "What should be documented?",
      "What can be learned from the case?",
    ],
    outro: [
      "I don't want you to simply memorize “difficult cases.” I want you to practice recognizing when a case deserves deeper review.",
    ],
  },
  {
    number: "VI",
    title: "The Breast Augmentation Case Learning System™",
    intro: [
      "A case shouldn't stop being useful when the review ends. You'll receive templates to build your own educational case-learning archive.",
    ],
    listLabel: "Including:",
    items: [
      "Case documentation",
      "Case reflection",
      "Case comparison",
      "Learning points",
      "Knowledge gaps",
      "Questions for future review",
      "Monthly case review",
      "Progress tracking",
      "Annual professional reflection",
    ],
    outro: [
      "Over time, you're not just reviewing cases. You're building a record of how your thinking develops.",
    ],
  },
];

export const tools = [
  { title: "The 15-Minute Case Analysis Sheet™", text: "The one-page version of the complete pathway." },
  {
    title: "The Pre-Case Review Checklist™",
    text: "The questions I recommend reviewing before beginning an educational case analysis.",
  },
  {
    title: "The Breast Augmentation Assessment Map™",
    text: "A visual framework for organizing your observations.",
  },
  {
    title: "The Case Comparison Matrix™",
    text: "A simple way to compare two educational cases side by side.",
  },
  {
    title: "The Visual Case Documentation Guide™",
    text: "A structured framework for organizing visual case documentation for educational review.",
  },
];

export type Bonus = {
  number: number;
  title: string;
  value: number;
  image: string;
  body: string[];
  list?: string[];
  closing?: string;
};

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: "30 Educational Breast Augmentation Cases™",
    value: 47,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/040ecb94-6a6d-47d6-946e-4688ee93bc4f.jpg",
    body: [
      "I wanted you to have more opportunities to practice the pathway.",
      "You'll receive 30 additional educational cases designed around the same process: Case → Think → Document → Review → Learn.",
    ],
    closing:
      "The goal isn't simply to read more cases. It's to practice your reasoning before reviewing the educational discussion.",
  },
  {
    number: 2,
    title: "The 10-Minute Breast Augmentation Case Review™",
    value: 37,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/9cb2843d-1b51-4100-8c2d-72ed140d1155.jpg",
    body: [
      "A compact version of the pathway for those moments when you don't have time for the complete review.",
      "10 minutes. One case. One structured review.",
    ],
    closing: "Use it when you want a quick but deliberate case-analysis exercise.",
  },
  {
    number: 3,
    title: "The Breast Augmentation Oversight Atlas™",
    value: 47,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/c055300f-c37b-455d-a7e2-1ebcf7d178ae.jpg",
    body: ["Educational scenarios focused on things that may be overlooked during case analysis. For each scenario:"],
    list: [
      "What happened?",
      "What might have been overlooked?",
      "What questions should be considered?",
      "What information deserves closer review?",
      "What is the educational lesson?",
    ],
    closing:
      "It's not about memorizing mistakes. It's about training yourself to look for what you might otherwise miss.",
  },
  {
    number: 4,
    title: "The Breast Augmentation Case Review Cards™",
    value: 37,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/da25e970-464e-4fe5-ace3-16a98c050a55.jpg",
    body: [
      "Printable case-review prompts you can use individually, with colleagues, or during educational case discussions. One card. One question. One focused review.",
    ],
    list: [
      "What do you actually observe?",
      "What information is missing?",
      "What deserves closer review?",
      "What assumptions are you making?",
      "What would you want to clarify?",
      "What did you learn?",
    ],
  },
  {
    number: 5,
    title: "The 30-Day Breast Augmentation Case Analysis Challenge™",
    value: 29,
    image: "https://cdn.magicpatterns.com/patterns/generated-images/2924292c-43e1-4151-aa89-7c79487bcdb1.jpg",
    body: [
      "Turn case review into a consistent habit. Every day: one case, one exercise, one reflection, one lesson.",
    ],
    closing: "By the end of 30 days, you'll have completed 30 structured case-review exercises.",
  },
];

export const beforeItems = [
  "You look at a new case and aren't sure where to begin.",
  "Every case feels like a completely different problem.",
  "Your case notes are inconsistent.",
  "You jump between different resources before organizing your thoughts.",
  "You finish a case without systematically extracting what you learned.",
];

export const afterItems = [
  "You have a repeatable case-review pathway.",
  "You can work through an educational case in approximately 15 minutes.",
  "You have standardized worksheets for your case reviews.",
  "You can compare cases using the same framework.",
  "You can identify information gaps and questions for further review.",
  "You can document what you learned from each case.",
];

export const testimonials = [
  {
    name: "Dr. Elena Rodríguez",
    role: "Plastic Surgeon",
    headline: "What I value most is the structure.",
    quote:
      "Instead of opening a case and immediately looking for the answer, the pathway forces me to explain what I actually see first.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/ef1c5b90-adfe-4914-8ef0-965fff78d712.jpg",
  },
  {
    name: "Dr. Richard Hale",
    role: "Aesthetic Physician",
    headline: "The 15-minute framework made case review much more deliberate.",
    quote: "I now have a consistent process instead of jumping between different notes and resources.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/1c16158f-6a3a-401e-a99a-66044c7b4590.jpg",
  },
  {
    name: "Dr. Mei Tanaka",
    role: "Plastic Surgery Fellow",
    headline: "The Case Atlas is what I use most.",
    quote: "Writing down my reasoning before reading the review makes me notice things I previously overlooked.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/358a6ada-f361-4c3d-9dbc-f243d47fa9c9.jpg",
  },
  {
    name: "Dr. Marcus Bennett",
    role: "Facial Plastic Surgeon",
    headline: "It doesn't feel like another textbook.",
    quote: "It feels like a tool I can actually use with a case.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/8f4e3066-e382-438f-8e13-28736cba68b9.jpg",
  },
  {
    name: "Dr. Claire Whitman",
    role: "Aesthetic Medicine Educator",
    headline: "The comparison matrix is particularly useful.",
    quote: "It gives everyone a common framework for discussing educational cases.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/699bc029-b5f7-4075-b1f6-a750ec56f3a6.jpg",
  },
];

export const faqs = [
  {
    q: "How will I receive the Breast Augmentation Clinical Pathway™?",
    a: "Immediately after your purchase, you'll receive access to the digital materials and downloadable resources included in the product.",
  },
  {
    q: "Who is the Breast Augmentation Clinical Pathway™ for?",
    a: "It is designed for qualified medical professionals seeking a structured framework for educational breast augmentation case review and professional learning.",
  },
  {
    q: "Is this a surgical technique course?",
    a: "No. The Clinical Pathway™ is an educational case-review framework. It does not provide individualized surgical instructions, treatment recommendations or a universal surgical recipe.",
  },
  {
    q: "Does the pathway tell me exactly what to do for every patient?",
    a: "No. Every patient and clinical situation is different. The system is designed to help organize educational case analysis, observations, questions and areas for further review.",
  },
  {
    q: "How long does each case take?",
    a: "The core pathway is designed around an approximately 15-minute educational case review.",
  },
  {
    q: "Can I print the materials?",
    a: "Yes. The worksheets, maps, checklists and case-review tools are designed to be downloaded and printed.",
  },
  {
    q: "Is this a one-time payment?",
    a: "Yes. The launch price is $27 USD, with no monthly subscription or recurring fees.",
  },
  {
    q: "What happens if I don't find it useful?",
    a: "You have a 7-day guarantee. You can explore the materials and decide whether the system fits your professional learning workflow.",
  },
];

export const buyers = [
  { name: "Dr. Sofía Martínez", country: "Mexico" },
  { name: "Dr. Alejandro Gómez", country: "Colombia" },
  { name: "Dr. Valentina Ruiz", country: "Argentina" },
  { name: "Dr. Carlos Herrera", country: "Spain" },
  { name: "Dr. Camila Torres", country: "Chile" },
  { name: "Dr. Diego Fernández", country: "Peru" },
  { name: "Dr. Lucía Morales", country: "Uruguay" },
  { name: "Dr. Mateo Castillo", country: "Ecuador" },
  { name: "Dr. Isabella Vargas", country: "Costa Rica" },
  { name: "Dr. Andrés Navarro", country: "Dominican Republic" },
  { name: "Dr. Gabriela Rojas", country: "Venezuela" },
  { name: "Dr. Javier Medina", country: "Guatemala" },
  { name: "Dr. Daniela Ortiz", country: "Panama" },
  { name: "Dr. Santiago Romero", country: "Paraguay" },
  { name: "Dr. Mariana Silva", country: "Bolivia" },
];
